// src/features/A3Metadata/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import i18n from "@/i18n";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// Import select options from the filter file (do not modify filter.tsx)
import { fieldKeyOptions } from "./filter";

// Zod schema: make fields optional and nullable to match the form behavior
const FormSchema = z.object({
  owner_id: z.coerce.number().optional().nullable(),
  owner_type: z.string().nullable().optional(),
  field_key: z.string().nullable().optional(),
  field_value: z.string().nullable().optional(),
});

export function A3MetadataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    disable_translations_request: true,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t("a3_metadata.title", {
                  ns: "tables",
                  defaultValue: "A3 Metadata",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Owner fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="owner_type"
                    label={i18n.t("a3_metadata.columns.owner_type", {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="owner_id"
                    label={i18n.t("a3_metadata.columns.owner_id", {
                      ns: "tables",
                      defaultValue: "Owner ID",
                    })}
                    type="number"
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Main inputs: Field Key and Field Value */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="field_key"
                    label={i18n.t("a3_metadata.columns.field_key", {
                      ns: "tables",
                      defaultValue: "Field Key",
                    })}
                    options={fieldKeyOptions}
                    control={form.control}
                    isRequired
                    allow_empty_option={false}
                  />
                  <InputField
                    name="field_value"
                    label={i18n.t("a3_metadata.columns.field_value", {
                      ns: "tables",
                      defaultValue: "Field Value",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
